import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { formatRupiah, formatDateShort } from "@/lib/format";
import { DollarSign, TrendingUp, CreditCard } from "lucide-react";

export default function FinanceReports({ isTabbed = false }) {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [year, setYear] = useState(new Date().getFullYear());

  const fetchOrders = async () => {
    setLoading(true);
    try {
      // Just fetch all and filter client side
      const { data } = await api.get('/orders');
      setOrders(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchOrders(); }, []);

  const filtered = orders.filter((o) => {
    // We base revenue off 'tanggal_masuk' or when it was paid
    // Let's use 'tanggal_masuk' for simplicity
    const d = new Date(o.tanggal_masuk || o.created_at);
    return (d.getMonth() + 1 === Number(month) && d.getFullYear() === Number(year));
  });

  const totalRevenue = filtered.reduce((sum, o) => sum + (o.total || 0), 0);
  const paidRevenue = filtered.reduce((sum, o) => {
    const paid = o.payment_status === "lunas" ? (o.total || 0) : (o.dp_amount || 0);
    return sum + paid;
  }, 0);
  const pendingRevenue = filtered.reduce((sum, o) => sum + (o.balance || 0), 0);

  const content = (
    <div className="space-y-6">
      <header className={`flex items-end justify-between border-b border-[#E5DCC7] pb-6 clf-rule ${isTabbed ? 'hidden' : ''}`}>
        <div>
          <div className="clf-label mb-2">Laporan</div>
          <h1 className="text-3xl tracking-tight font-medium">Laporan Keuangan</h1>
          <p className="text-sm text-stone-500 mt-1">Ringkasan pendapatan bulan ini.</p>
        </div>
      </header>

      {/* Date filters moved outside header when tabbed so it's always visible */}
      <div className={`flex items-center gap-2 border border-[#D9CDB5] bg-white p-1 w-fit ${isTabbed ? '' : '-mt-[76px] ml-auto relative z-10'}`}>
        <select value={month} onChange={(e) => setMonth(e.target.value)} className="px-3 py-1.5 text-sm bg-transparent focus:outline-none">
          {Array.from({length: 12}, (_, i) => i + 1).map(m => (
            <option key={m} value={m}>{new Date(2000, m - 1).toLocaleString('id-ID', { month: 'long' })}</option>
          ))}
        </select>
        <select value={year} onChange={(e) => setYear(e.target.value)} className="px-3 py-1.5 text-sm bg-transparent focus:outline-none border-l border-[#E5DCC7]">
          {Array.from({length: 5}, (_, i) => new Date().getFullYear() - i).map(y => (
            <option key={y} value={y}>{y}</option>
          ))}
        </select>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white border border-[#E5DCC7] p-5">
          <div className="flex items-start justify-between mb-3">
            <div className="clf-label">Total Omset</div>
            <DollarSign className="h-4 w-4 text-stone-400" strokeWidth={1.5} />
          </div>
          <div className="text-2xl font-medium font-mono tracking-tight text-stone-900">
            {formatRupiah(totalRevenue)}
          </div>
        </div>
        <div className="bg-white border border-[#E5DCC7] p-5">
          <div className="flex items-start justify-between mb-3">
            <div className="clf-label">Uang Masuk (Lunas & DP)</div>
            <TrendingUp className="h-4 w-4 text-emerald-600" strokeWidth={1.5} />
          </div>
          <div className="text-2xl font-medium font-mono tracking-tight text-emerald-700">
            {formatRupiah(paidRevenue)}
          </div>
        </div>
        <div className="bg-white border border-[#E5DCC7] p-5">
          <div className="flex items-start justify-between mb-3">
            <div className="clf-label">Sisa Tagihan (Belum Lunas)</div>
            <CreditCard className="h-4 w-4 text-stone-400" strokeWidth={1.5} />
          </div>
          <div className="text-2xl font-medium font-mono tracking-tight text-[#A63D30]">
            {formatRupiah(pendingRevenue)}
          </div>
        </div>
      </div>
      
      <div className="bg-white border border-[#E5DCC7] mt-8">
        <table className="w-full text-sm">
          <thead className="border-b border-[#E5DCC7] bg-stone-50">
            <tr>
              <th className="text-left clf-label px-4 py-3">No. Order</th>
              <th className="text-left clf-label px-4 py-3">Customer</th>
              <th className="text-left clf-label px-4 py-3">Tgl Masuk</th>
              <th className="text-right clf-label px-4 py-3">Total</th>
              <th className="text-right clf-label px-4 py-3">Telah Dibayar</th>
              <th className="text-right clf-label px-4 py-3">Sisa</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} className="px-4 py-10 text-center text-stone-500">Memuat...</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={6} className="px-4 py-12 text-center text-stone-500">Belum ada transaksi di bulan ini.</td></tr>
            ) : (
              filtered.map((o) => {
                const paid = o.payment_status === "lunas" ? (o.total || 0) : (o.dp_amount || 0);
                return (
                  <tr key={o.id} className="border-b border-stone-100 last:border-0 hover:bg-stone-50">
                    <td className="px-4 py-3 font-mono text-stone-900 font-medium">{o.order_number}</td>
                    <td className="px-4 py-3">
                      <div className="text-stone-900">{o.customer?.name || "—"}</div>
                    </td>
                    <td className="px-4 py-3 font-mono text-stone-700">{formatDateShort(o.tanggal_masuk)}</td>
                    <td className="px-4 py-3 text-right font-mono text-stone-700">{formatRupiah(o.total)}</td>
                    <td className="px-4 py-3 text-right font-mono text-emerald-700">{formatRupiah(paid)}</td>
                    <td className="px-4 py-3 text-right font-mono text-[#A63D30]">{formatRupiah(o.balance)}</td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );

  if (isTabbed) return content;
  
  return (
    <div className="p-4 lg:p-10 space-y-6 max-w-[1400px]">
      {content}
    </div>
  );
}
